/**
 * Production-Safe Logger Utility
 *
 * Replaces console.log/error/warn with environment-aware logging
 * - Development: Full logging to console
 * - Production: Silent or minimal logging (can integrate with Sentry/LogRocket later)
 *
 * Usage:
 *   import logger from './utils/logger';
 *   logger.info('Message'); // Only logs in development
 *   logger.error('Error');  // Always logs errors (important!)
 *   logger.debug('Debug');  // Only in development
 */

const isDevelopment = process.env.NODE_ENV === 'development' ||
                      process.env.REACT_APP_ENABLE_LOGS === 'true';

class Logger {
  /**
   * Info level - general information
   * Only logs in development mode
   */
  info(...args) {
    if (isDevelopment) {
      console.log(...args);
    }
  }

  /**
   * Debug level - detailed debugging information
   * Only logs in development mode
   */
  debug(...args) {
    if (isDevelopment) {
      console.log(...args);
    }
  }

  /**
   * Warning level - something unexpected but not critical
   * Logs in all environments (but can be filtered in production)
   */
  warn(...args) {
    if (isDevelopment) {
      console.warn(...args);
    } else {
      // In production, you might want to send to error tracking service
      // Example: Sentry.captureMessage(args.join(' '), 'warning');
    }
  }

  /**
   * Error level - something went wrong
   * ALWAYS logs (critical for debugging production issues)
   */
  error(...args) {
    console.error(...args);
    // In production, send to error tracking service
    // Example: Sentry.captureException(args[0]);
  }

  /**
   * Success level - operation completed successfully
   * Only logs in development mode
   */
  success(...args) {
    if (isDevelopment) {
      console.log('✅', ...args);
    }
  }

  /**
   * Grouped logs - for collapsible log groups
   * Only in development
   */
  group(label, callback) {
    if (isDevelopment) {
      console.group(label);
      callback();
      console.groupEnd();
    } else {
      // Still execute callback, just don't log
      callback();
    }
  }

  /**
   * Table logs - for structured data
   * Only in development
   */
  table(data) {
    if (isDevelopment) {
      console.table(data);
    }
  }

  /**
   * Time tracking - measure performance
   * Only in development
   */
  time(label) {
    if (isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
const logger = new Logger();

export default logger;

// Named exports for convenience
export const { info, debug, warn, error, success, group, table, time, timeEnd } = logger;
